<template>
  <div
    data-test="us-input-calendar"
    class="us-input us-input--calendar"
    :class="{
      'us-input--error': error,
      'us-input--disabled': disabled,
      'us-input--calendar--top': popupPlacement === 'top'
    }"
  >
    <component
      :is="htmlHeadingTag"
      v-if="heading"
      class="display--d2--semibold input-heading-wrapper"
    >
      {{ heading }}
    </component>
    <slot name="heading" />
    <p v-if="description" class="body--p1--regular input-description-wrapper">
      {{ description }}
    </p>
    <slot name="description" />

    <div class="us-input-border body--p1--regular">
      <ClientOnly>
        <v-date-picker
          :model-value="modelValue"
          color="purple"
          is-range
          :min-date="minDate"
          :max-date="maxDate"
          :masks="{
            input: ['L', 'YYYY/MM/DD', 'YYYY/M/D', 'MMMM DD, YYYY'],
            L: 'MMM DD, YYYY'
          }"
          :mode="includeTime ? 'dateTime' : 'date'"
          :popover="{
            placement: `bottom-end`,
            visibility: 'click'
          }"
          @update:modelValue="$emit('update:modelValue', $event)"
        >
          <template #default="{ inputValue, inputEvents }">
            <div
              class="input-wrapper"
              :class="{
                'input-wrapper--placeholder': showPlaceHolder(
                  inputValue.start,
                  inputValue.end
                )
              }"
            >
              <input
                size="100000"
                :value="formattedDateRange(inputValue.start, inputValue.end)"
                readonly
                style="cursor: pointer; width: 100%"
                v-on="inputEvents.start"
              />
            </div>
          </template>
        </v-date-picker>
      </ClientOnly>
      <USIcon name="calendar" css-diameter="var(--diameter)" />
    </div>

    <div v-if="error" class="error-msg body--p1--regular">{{ error }}</div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue'

import USIcon from '../USIcon/USIcon.vue'
import ClientOnly from '../ClientOnly/ClientOnly.vue'

export default defineComponent({
  name: 'USInputdate',
  components: {
    USIcon,
    ClientOnly
  },
  props: {
    modelValue: {
      type: Object as PropType<{ start: Date | null; end: Date | null }>,
      default: null
    },
    minDate: {
      type: Object as PropType<Date> | null,
      default: null
    },
    maxDate: {
      type: Object as PropType<Date> | null,
      default: null
    },
    error: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    heading: {
      type: String,
      default: ''
    },
    headingLevel: {
      type: Number as PropType<1 | 2 | 3 | 4 | 5 | 6>,
      default: 1
    },
    description: {
      type: String,
      default: ''
    },
    popupPlacement: {
      type: String,
      default: 'bottom',
      validator: (val: string) => ['top', 'bottom'].includes(val)
    },
    placeholder: {
      type: String,
      default: 'Select a date range'
    },
    includeTime: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  setup(props) {
    const htmlHeadingTag = computed(() => `h${props.headingLevel}`)

    const formattedDateRange = (start: string, end: string) => {
      if (start && end) return `${start} to ${end}`
      return props.placeholder
    }

    const showPlaceHolder = (start: string, end: string) => {
      return !(start && end)
    }

    return { formattedDateRange, showPlaceHolder, htmlHeadingTag }
  }
})
</script>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint' as bp;
@use '@base/styles/v1.0/scss/foundations/motion';

.us-input--error {
  .us-input-border {
    border-color: var(--color--semantic--error-2) !important;
  }
}

.us-input--disabled {
  &:hover,
  &:focus {
    cursor: not-allowed;
  }

  .us-input-border {
    border-color: transparent !important;
    background-color: var(--color--neutral-a4) !important;
    color: var(--color--neutral-a3);
  }

  input {
    pointer-events: none;
  }

  .us-icon :deep(svg) {
    color: var(--color--primary-b1);
  }
}

.us-input-border {
  background-color: var(--color--neutral-a5);
  border: var(--line-weight--light) solid var(--color--primary-b1);
  border-radius: var(--border-radius--small);
  // overflow: hidden;
  padding: var(--spacing--7);
  position: relative;
  transition: motion.duration(2);
  width: 100%;

  &:hover,
  &:focus {
    // border: var(--line-weight--heavy) solid var(--color--primary-b1);
    box-shadow: inset 0px 0px 0px 1px var(--color--primary-b1);
  }
}

input {
  background-color: transparent;
  color: inherit;
}

.us-icon {
  --diameter: 17px;

  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: var(--spacing--7);
  pointer-events: none;
  z-index: 4;

  ::v-deep(svg) {
    color: var(--color--primary-b1);
  }

  @media (min-width: bp.$breakpoint--desktop) {
    --diameter: 21px;
  }
}

:deep(.vc-popover-content-wrapper) {
  left: initial !important;
  right: -1px !important;
  top: calc(100% + var(--spacing--6)) !important;
  transform: translate(0, 0) !important;
  width: 252px;

  .vc-popover-content-wrapper {
    display: none;
  }

  .us-input--calendar--top & {
    bottom: calc(100% + var(--spacing--6)) !important;
    top: initial !important;
  }

  .vc-popover-content {
    border: none;
    border-radius: var(--border-radius--small);
    box-shadow: var(--box-shadow--b1);
  }

  .vc-container {
    font-family: var(--font-family-primary) !important;
  }

  .vc-popover-caret {
    display: none;
  }

  .vc-weeks {
    padding: var(--spacing--7);
  }

  .vc-title {
    color: var(--color--primary-b1);
    font-size: var(--font-size--z5);
    font-weight: var(--font-weight--medium);
  }

  .vc-weekday {
    border-bottom: var(--line-weight--light) solid var(--color--neutral-a4);
    color: var(--color--neutral-a1);
    font-size: var(--font-size--z6);
    font-weight: var(--font-weight--regular);
  }

  .vc-day {
    // Highlight bg between
    --purple-200: var(--color--secondary-c2) !important;

    // Bg color start & end
    --purple-600: var(--color--primary-b1) !important;

    // border color when selected
    --purple-700: var(--color--primary-b1) !important;

    .vc-day-content {
      color: var(--color--neutral-a1);
      font-size: var(--font-size--z7);
      font-weight: var(--font-weight-regular) !important;
    }

    &.is-today .vc-day-content {
      border: var(--line-weight--light) solid var(--color--secondary-c1);
      border-radius: 50%;
    }

    .is-disabled {
      color: var(--color--neutral-a3);
      cursor: not-allowed;

      &:hover {
        background: inherit;
      }
    }
  }
}

.input-heading-wrapper {
  margin-bottom: var(--spacing--8);
}

.input-description-wrapper {
  margin-bottom: var(--spacing--7);
}

.error-msg {
  color: var(--color--semantic--error-2);
  padding-top: var(--spacing--8);
}

.input-wrapper {
  overflow: hidden;

  &--placeholder {
    color: var(--color--neutral-a1);
  }
}

:deep(.vc-arrow) {
  color: var(--color--primary-b3);

  &:hover {
    border-radius: 50%;
  }
}

:deep(.vc-am-pm button.active) {
  background: var(--color--primary-b1);
  &:focus {
    border-color: var(--color--primary-b1);
  }
}

// :deep(.vc-time-month) {
//   color: var(--color-primary-80);
// }

// :deep(.vc-time-day) {
//   color: var(--color-primary-80);
// }

:deep(.vc-popover-content-wrapper) {
  top: calc(100% + var(--spacing--6)) !important;
  right: -4% !important;
}
</style>
